import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Mail,
  Phone,
  MapPin,
  Building,
  Brain,
  Globe,
  X,
  Loader2,
  Edit2,
  AlertCircle,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import api, { Client } from '../services/api';

interface ClientFormData {
  name: string;
  company: string;
  industry: string;
  industry_name: string;
  email: string;
  phone: string;
  location: string;
  status: 'internal' | 'active' | 'pending' | 'completed' | 'inactive';
  google_exposure_model: '' | 'sab_single' | 'sab_multi' | 'brand';
  scale_pattern: '' | 'single_market' | 'multi_market_finite' | 'multi_market_programmatic';
  demand_modes: ('maps' | 'organic' | 'ai' | 'ads')[];
  notes: string;
}

const initialFormData: ClientFormData = {
  name: '',
  company: '',
  industry: '',
  industry_name: '',
  email: '',
  phone: '',
  location: '',
  status: 'pending',
  google_exposure_model: '',
  scale_pattern: '',
  demand_modes: [],
  notes: ''
};

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPhase, setSelectedPhase] = useState('all');

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [formData, setFormData] = useState<ClientFormData>(initialFormData);
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [showTransitionConfirm, setShowTransitionConfirm] = useState(false);
  const [clientToTransition, setClientToTransition] = useState<Client | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [verticals, setVerticals] = useState<Array<{ id: string; name: string; category: string }>>([]); 

  const fetchClients = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await api.getClients({
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        search: searchQuery || undefined
      });
      setClients(response.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch clients');
    } finally {
      setIsLoading(false);
    }
  }, [selectedStatus, searchQuery]);

  useEffect(() => {
    fetchClients();
    api.getIndustries().then(r => setVerticals((r.results || r) as any)).catch(() => {});
  }, [fetchClients]);

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'status-badge status-active';
      case 'pending':
        return 'status-badge status-warning';
      case 'completed':
        return 'status-badge status-completed';
      case 'inactive':
        return 'status-badge status-paused';
      case 'internal':
        return 'status-badge bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300';
      default:
        return 'status-badge';
    }
  };

  const openAddModal = () => {
    setFormData(initialFormData);
    setModalMode('add');
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (client: Client) => {
    setFormData({
      name: client.name,
      company: client.company,
      industry: client.industry || "",
      industry_name: client.industry_display || client.industry_name,
      email: client.email,
      phone: client.phone,
      location: client.location,
      status: client.status,
      google_exposure_model: client.google_exposure_model || '',
      scale_pattern: client.scale_pattern || '',
      demand_modes: client.demand_modes || [],
      notes: client.notes
    });
    setEditingClientId(client.id);
    setModalMode('edit');
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');

    try {
      if (modalMode === 'add') {
        await api.createClient({ ...formData, industry: formData.industry || null });
      } else if (editingClientId) {
        await api.updateClient(editingClientId, { ...formData, industry: formData.industry || null });
      }
      setShowModal(false);
      fetchClients();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save client');
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleTransition = async () => {
    if (!clientToTransition) return;
    setIsTransitioning(true);
    try {
      await api.transitionClientPhase(clientToTransition.id);
      setShowTransitionConfirm(false);
      setClientToTransition(null);
      fetchClients();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to transition client');
    } finally {
      setIsTransitioning(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesIndustry = selectedIndustry === 'all' ||
      client.industry_display?.toLowerCase() === selectedIndustry.toLowerCase() ||
      client.industry_name?.toLowerCase() === selectedIndustry.toLowerCase();
    const matchesPhase = selectedPhase === 'all' || client.phase === selectedPhase;
    return matchesIndustry && matchesPhase;
  }).sort((a, b) => {
    if (a.status === 'internal' && b.status !== 'internal') return -1;
    if (b.status === 'internal' && a.status !== 'internal') return 1;
    return 0;
  });

  const industries = Array.from(new Set(clients.map(c => c.industry_display || c.industry_name).filter(Boolean)));

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header - Compact */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-base font-semibold text-gray-900 dark:text-white">Client Management</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Manage your AI and Marketing clients</p>
            </div>
            <button onClick={openAddModal} className="btn btn-primary">
              <Plus className="w-4 h-4" />
              Add Client
            </button>
          </div>
        </div>
      </div>

      {/* Filters - Compact */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search clients..."
                className="input pl-7"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <select
              className="select"
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
            >
              <option value="all">All Industries</option>
              {industries.map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
            <select
              className="select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="internal">Internal</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              className="select"
              value={selectedPhase}
              onChange={(e) => setSelectedPhase(e.target.value)}
            >
              <option value="all">All Phases</option>
              <option value="initial">Initial</option>
              <option value="cruising">Cruising</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="bg-status-error/10 border border-status-error/30 rounded p-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-status-error" />
            <span className="text-xs text-status-error">{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="max-w-7xl mx-auto px-3 py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      ) : (
        /* Clients Grid - Compact */
        <div className="max-w-7xl mx-auto px-3 py-3">
          {filteredClients.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-xs text-gray-500 dark:text-gray-400">No clients found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  onClick={() => navigate(`/clients/${client.id}`)}
                  className={`panel cursor-pointer transition-colors ${
                    client.status === 'internal'
                      ? 'hover:border-primary-300 dark:hover:border-primary-700'
                      : 'hover:border-primary-300 dark:hover:border-primary-700'
                  }`}
                  style={client.status === 'internal' ? { backgroundColor: '#fef9c3', borderColor: '#facc15' } : undefined}
                >
                  <div className="p-3">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{client.name}</h3>
                          <span className={getStatusClass(client.status)}>
                            {client.status}
                          </span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                            client.phase === 'cruising'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          }`}>
                            {client.phase === 'cruising' ? 'Cruising' : 'Initial'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{client.industry_display || client.industry_name}</p>
                        {(client.google_exposure_model || client.demand_modes?.length > 0) && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {client.google_exposure_model && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                {client.google_exposure_model.replace(/_/g, ' ').toUpperCase()}
                              </span>
                            )}
                            {client.scale_pattern && client.scale_pattern !== 'single_market' && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                                {client.scale_pattern.replace(/_/g, ' ')}
                              </span>
                            )}
                            {client.demand_modes?.map((mode: string) => (
                              <span key={mode} className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">
                                {mode}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {client.phase === 'initial' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setClientToTransition(client);
                              setShowTransitionConfirm(true);
                            }}
                            className="p-1 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition-colors"
                            title="Transition to Cruising"
                          >
                            <ArrowRight className="w-4 h-4 text-green-600" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditModal(client); }}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                        >
                          <Edit2 className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    </div>

                    {/* Contact Info - Compact Grid */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 mb-2 text-xs">
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Building className="w-3 h-3 text-gray-400" />
                        <span className="truncate">{client.company}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        <span className="truncate">{client.location || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Mail className="w-3 h-3 text-gray-400" />
                        <span className="truncate">{client.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <Phone className="w-3 h-3 text-gray-400" />
                        <span className="truncate">{client.phone || 'N/A'}</span>
                      </div>
                    </div>

                    {/* Services - Compact */}
                    {client.services && client.services.length > 0 && (
                      <div className="mb-2">
                        <div className="flex flex-wrap gap-1">
                          {client.services.map((service, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                            >
                              {service.service_type === 'AI' ? (
                                <Brain className="w-3 h-3" />
                              ) : (
                                <Globe className="w-3 h-3" />
                              )}
                              {service.service_name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Projects - Compact */}
                    {client.projects && client.projects.length > 0 && (
                      <div className="mb-2">
                        <div className="space-y-1">
                          {client.projects.slice(0, 2).map((project) => (
                            <div key={project.id} className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-2">
                                {project.project_type === 'AI' ? (
                                  <Brain className="w-3 h-3 text-purple-500" />
                                ) : (
                                  <Globe className="w-3 h-3 text-blue-500" />
                                )}
                                <span className="text-gray-600 dark:text-gray-300 truncate max-w-[120px]">{project.name}</span>
                                <span className={getStatusClass(project.status)}>{project.status}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-12 h-1 bg-gray-200 dark:bg-gray-700 rounded-full">
                                  <div
                                    className="h-full bg-primary-500 rounded-full"
                                    style={{ width: `${project.progress}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500">{project.progress}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Metrics - Compact */}
                    <div className="border-t border-light-border dark:border-dark-border pt-2">
                      <div className="grid grid-cols-4 gap-2">
                        <div className="text-center">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            ${parseFloat(client.total_spent || '0').toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500">Spent</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{client.completed_projects_count}</p>
                          <p className="text-xs text-gray-500">Done</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{client.active_projects_count}</p>
                          <p className="text-xs text-gray-500">Active</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {new Date(client.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                          <p className="text-xs text-gray-500">Updated</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal - Compact */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto border border-light-border dark:border-dark-border">
            <div className="flex items-center justify-between p-3 border-b border-light-border dark:border-dark-border">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {modalMode === 'add' ? 'Add New Client' : 'Edit Client'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-3 space-y-3">
              {formError && (
                <div className="bg-status-error/10 border border-status-error/30 rounded p-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-status-error" />
                  <span className="text-xs text-status-error">{formError}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Company *</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="input"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Industry</label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="select"
                  >
                    <option value="">Select industry...</option>
                    {Object.entries(verticals.reduce((acc: Record<string, typeof verticals>, v) => {
                      const cat = v.category || 'other';
                      if (!acc[cat]) acc[cat] = [];
                      acc[cat].push(v);
                      return acc;
                    }, {})).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
                      <optgroup key={category} label={category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}>
                        {items.sort((a, b) => a.name.localeCompare(b.name)).map(v => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="input"
                    placeholder="e.g., New York"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ClientFormData['status'] })}
                  className="select"
                >
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Classification */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Classification</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Google Exposure</label>
                    <select
                      value={formData.google_exposure_model}
                      onChange={(e) => setFormData({ ...formData, google_exposure_model: e.target.value as ClientFormData['google_exposure_model'] })}
                      className="select"
                    >
                      <option value="">Not set</option>
                      <option value="sab_single">SAB Single</option>
                      <option value="sab_multi">SAB Multi</option>
                      <option value="brand">Brand</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Scale Pattern</label>
                    <select
                      value={formData.scale_pattern}
                      onChange={(e) => setFormData({ ...formData, scale_pattern: e.target.value as ClientFormData['scale_pattern'] })}
                      className="select"
                    >
                      <option value="">Not set</option>
                      <option value="single_market">Single Market</option>
                      <option value="multi_market_finite">Multi-Market Finite</option>
                      <option value="multi_market_programmatic">Multi-Market Programmatic</option>
                    </select>
                  </div>
                </div>
                <div className="mt-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Demand Modes</label>
                  <div className="flex gap-3">
                    {['maps', 'organic', 'ai', 'ads'].map((mode) => (
                      <label key={mode} className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.demand_modes.includes(mode as 'maps' | 'organic' | 'ai' | 'ads')}
                          onChange={(e) => {
                            const modes = e.target.checked
                              ? [...formData.demand_modes, mode as 'maps' | 'organic' | 'ai' | 'ads']
                              : formData.demand_modes.filter((m) => m !== mode);
                            setFormData({ ...formData, demand_modes: modes });
                          }}
                          className="rounded border-gray-300"
                        />
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="input"
                  placeholder="Additional notes..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {modalMode === 'add' ? 'Add Client' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transition Phase Confirmation Modal */}
      {showTransitionConfirm && clientToTransition && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-sm w-full p-4 border border-light-border dark:border-dark-border">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full">
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Transition to Cruising</h2>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300 mb-1">
              Move <strong>{clientToTransition.name}</strong> from <strong>Initial</strong> to <strong>Cruising</strong> phase?
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              This means setup is complete and the client moves to ongoing operations. This action cannot be reversed.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowTransitionConfirm(false); setClientToTransition(null); }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleTransition}
                disabled={isTransitioning}
                className="btn bg-green-600 text-white hover:bg-green-700"
              >
                {isTransitioning && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirm Transition
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ClientsPage;
